import { IMPERSONATION_ACTIVITY_PERSIST_THROTTLE_MS, IMPERSONATION_INACTIVITY_TIMEOUT_MS } from '@/constants';
import { type ApplyImpersonationResult, type ImpersonationSession, ImpersonationWarningCode } from '@/types';

const IMPERSONATION_STORAGE_KEY = 'impersonation-session';
const IMPERSONATION_LAST_ACTIVITY_STORAGE_KEY = 'impersonation-last-activity';
const IMPERSONATION_EMAIL_HEADER = 'X-Impersonate-User';
const IMPERSONATION_NAME_STATE_KEY = 'impersonatedUserName';
const IS_OVERALL_ADMIN_STATE_KEY = 'isOverallAdmin';
const RETURN_URL_ALLOWLIST = import.meta.env.VITE_CONTROL_CENTER_URL || '';
const CONTROL_CENTER_DEFAULT_URL = import.meta.env.VITE_CONTROL_CENTER_URL || '';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_IMPERSONATION_SESSION: ImpersonationSession = {
  impersonatedUserEmail: null,
  impersonatedUserName: null,
  returnToControlCenterUrl: null,
  churchId: null,
  isOverallAdmin: false,
  isImpersonating: false,
};

type Subscriber = () => void;

const subscribers = new Set<Subscriber>();

const parseAllowedOrigins = (): Set<string> => {
  return new Set(
    RETURN_URL_ALLOWLIST.split(',')
      .map((origin: string) => origin.trim())
      .filter(Boolean)
  );
};

const allowedOrigins = parseAllowedOrigins();

const parseStoredSession = (): ImpersonationSession => {
  if (typeof window === 'undefined') {
    return DEFAULT_IMPERSONATION_SESSION;
  }

  try {
    const rawSession = window.sessionStorage.getItem(IMPERSONATION_STORAGE_KEY);
    if (!rawSession) {
      return DEFAULT_IMPERSONATION_SESSION;
    }

    const parsedSession = JSON.parse(rawSession) as Partial<ImpersonationSession>;
    const impersonatedUserEmail = parsedSession.impersonatedUserEmail ?? null;
    const impersonatedUserName = parsedSession.impersonatedUserName ?? null;
    const returnToControlCenterUrl = parsedSession.returnToControlCenterUrl ?? null;
    const churchId =
      typeof parsedSession.churchId === 'string' && parsedSession.churchId.trim()
        ? parsedSession.churchId.trim()
        : null;
    const isOverallAdmin = parsedSession.isOverallAdmin === true;
    const isImpersonating = Boolean(impersonatedUserEmail && parsedSession.isImpersonating);

    return {
      impersonatedUserEmail,
      impersonatedUserName,
      returnToControlCenterUrl,
      churchId,
      isOverallAdmin,
      isImpersonating,
    };
  } catch (_) {
    return DEFAULT_IMPERSONATION_SESSION;
  }
};

let currentSession: ImpersonationSession = parseStoredSession();
let lastActivityPersistedAt = 0;

const persistSession = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(currentSession));
};

const getLastActivityAt = (): number | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const rawValue = window.sessionStorage.getItem(IMPERSONATION_LAST_ACTIVITY_STORAGE_KEY);
  if (!rawValue) {
    return null;
  }

  const parsedValue = Number(rawValue);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const writeLastActivityAt = ({ timestamp }: { timestamp: number }): void => {
  if (typeof window === 'undefined') {
    return;
  }

  lastActivityPersistedAt = timestamp;
  window.sessionStorage.setItem(IMPERSONATION_LAST_ACTIVITY_STORAGE_KEY, timestamp.toString());
};

const clearLastActivityAt = (): void => {
  lastActivityPersistedAt = 0;
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(IMPERSONATION_LAST_ACTIVITY_STORAGE_KEY);
};

// Throttle storage writes
const recordActivity = (): void => {
  if (!currentSession.isImpersonating) {
    return;
  }

  const now = Date.now();
  if (now - lastActivityPersistedAt < IMPERSONATION_ACTIVITY_PERSIST_THROTTLE_MS) {
    return;
  }

  writeLastActivityAt({ timestamp: now });
};

const isInactivityTimeoutReached = (): boolean => {
  if (!currentSession.isImpersonating) {
    return false;
  }

  const lastActivityAt = getLastActivityAt();
  if (lastActivityAt === null) {
    return true;
  }

  return Date.now() - lastActivityAt >= IMPERSONATION_INACTIVITY_TIMEOUT_MS;
};

const notifySubscribers = (): void => {
  subscribers.forEach((subscriber) => subscriber());
};

const updateSession = ({ session }: { session: ImpersonationSession }): void => {
  currentSession = session;
  persistSession();
  notifySubscribers();
};

const getStateObject = (rawState: unknown): Record<string, unknown> | null => {
  if (!rawState) {
    return null;
  }

  if (typeof rawState === 'string') {
    try {
      const parsedState = JSON.parse(rawState);
      return getStateObject(parsedState);
    } catch (_) {
      return null;
    }
  }

  if (typeof rawState !== 'object' || Array.isArray(rawState)) {
    return null;
  }

  const stateObject = rawState as Record<string, unknown>;

  if ('userState' in stateObject) {
    const nestedStateObject = getStateObject(stateObject.userState);
    if (nestedStateObject) {
      return nestedStateObject;
    }
  }

  return stateObject;
};

const getValidatedReturnUrl = ({ rawValue }: { rawValue: unknown }): string | null => {
  if (typeof rawValue !== 'string' || !rawValue.trim()) {
    return null;
  }

  try {
    const parsedUrl = new URL(rawValue);
    if (!allowedOrigins.has(parsedUrl.origin)) {
      return null;
    }

    return parsedUrl.toString();
  } catch (_) {
    return null;
  }
};

const getConfiguredControlCenterUrl = (): string | null => {
  const normalizedControlCenterUrl = CONTROL_CENTER_DEFAULT_URL.trim().replace(/^['"]|['"]$/g, '');
  if (!normalizedControlCenterUrl) {
    return null;
  }

  try {
    return new URL(normalizedControlCenterUrl).toString();
  } catch (_) {
    return null;
  }
};

const hasValidEmail = ({ email }: { email: string }): boolean => EMAIL_PATTERN.test(email);

const setSession = ({
  impersonatedUserEmail,
  impersonatedUserName,
  returnToControlCenterUrl,
  churchId = null,
  isOverallAdmin = false,
}: {
  impersonatedUserEmail: string;
  impersonatedUserName: string | null;
  returnToControlCenterUrl: string | null;
  churchId?: string | null;
  isOverallAdmin?: boolean;
}): void => {
  updateSession({
    session: {
      impersonatedUserEmail,
      impersonatedUserName,
      returnToControlCenterUrl,
      churchId,
      isOverallAdmin,
      isImpersonating: true,
    },
  });
};

const clearSession = (): void => {
  clearLastActivityAt();
  updateSession({ session: DEFAULT_IMPERSONATION_SESSION });
};

const applyFromOidcState = ({ state }: { state: unknown }): ApplyImpersonationResult => {
  const stateObject = getStateObject(state);
  if (!stateObject) {
    return { warningCode: null };
  }

  const rawEmail = stateObject[IMPERSONATION_EMAIL_HEADER];
  const rawImpersonatedUserName = stateObject[IMPERSONATION_NAME_STATE_KEY];
  const rawReturnToControlCenterUrl = stateObject.returnToControlCenterUrl;
  const isImpersonationRequested =
    rawEmail !== undefined || rawImpersonatedUserName !== undefined || rawReturnToControlCenterUrl !== undefined;

  if (!isImpersonationRequested) {
    clearSession();
    return { warningCode: null };
  }

  if (typeof rawEmail !== 'string' || !rawEmail.trim()) {
    clearSession();
    return { warningCode: ImpersonationWarningCode.MissingEmail };
  }

  const normalizedEmail = rawEmail.trim().toLowerCase();
  if (!hasValidEmail({ email: normalizedEmail })) {
    clearSession();
    return { warningCode: ImpersonationWarningCode.InvalidEmail };
  }
  const normalizedImpersonatedUserName =
    typeof rawImpersonatedUserName === 'string' && rawImpersonatedUserName.trim()
      ? rawImpersonatedUserName.trim()
      : null;

  const returnToControlCenterUrl = getValidatedReturnUrl({ rawValue: rawReturnToControlCenterUrl });
  const hasInvalidReturnUrl = rawReturnToControlCenterUrl !== undefined && rawReturnToControlCenterUrl !== null;
  const warningCode =
    hasInvalidReturnUrl && !returnToControlCenterUrl ? ImpersonationWarningCode.InvalidReturnUrl : null;
  const rawIsOverallAdmin = stateObject[IS_OVERALL_ADMIN_STATE_KEY];
  const isOverallAdmin = rawIsOverallAdmin === 'true' || rawIsOverallAdmin === true;

  setSession({
    impersonatedUserEmail: normalizedEmail,
    impersonatedUserName: normalizedImpersonatedUserName,
    returnToControlCenterUrl,
    isOverallAdmin,
  });

  writeLastActivityAt({ timestamp: Date.now() });

  return { warningCode };
};

const subscribe = ({ listener }: { listener: Subscriber }): (() => void) => {
  subscribers.add(listener);
  return () => subscribers.delete(listener);
};

const getSession = (): ImpersonationSession => currentSession;

const setChurchId = ({ churchId }: { churchId: string | null }): void => {
  if (!currentSession.isImpersonating) {
    return;
  }

  const normalizedChurchId = churchId?.trim() || null;
  if (currentSession.churchId === normalizedChurchId) {
    return;
  }

  updateSession({
    session: {
      ...currentSession,
      churchId: normalizedChurchId,
    },
  });
};

const getImpersonatedUserEmail = (): string | null => {
  if (!currentSession.isImpersonating) {
    return null;
  }

  return currentSession.impersonatedUserEmail;
};

const getStopRedirectUrl = (): string | null =>
  currentSession.returnToControlCenterUrl || getConfiguredControlCenterUrl();

export const impersonationService = {
  applyFromOidcState,
  clearSession,
  subscribe,
  getSession,
  setChurchId,
  getImpersonatedUserEmail,
  getStopRedirectUrl,
  recordActivity,
  isInactivityTimeoutReached,
};
